import React from 'react';

/** 与界面预览、canvas 下载水印共用，避免各处文案不一致 */
export const WATERMARK_TEXT = 'Nemolightlab-AI';

/** 小鱼矢量：`public/nemo-logo.svg`（鱼轮廓 + 体内 NEMO 笔画 + 鱼尾） */
export const WATERMARK_FISH_SVG_SRC = `${import.meta.env.BASE_URL}nemo-logo.svg`;

/** @deprecated 请用 WATERMARK_FISH_SVG_SRC */
export const WATERMARK_MARK_SRC = WATERMARK_FISH_SVG_SRC;

/** 生成图下载文件名统一前缀（浏览器另存为 / COS Content-Disposition） */
export const GENERATED_IMAGE_DOWNLOAD_BASENAME_PREFIX = 'Nemolightlab-AI';

/** 生成结果下载文件名：`Nemolightlab-AI-<唯一部分>.png`（与 canvas.toDataURL PNG 一致） */
export function generatedImageDownloadFilename(uniquePart: string | number): string {
  const id = String(uniquePart).replace(/[^a-zA-Z0-9._-]/g, '_');
  return `${GENERATED_IMAGE_DOWNLOAD_BASENAME_PREFIX}-${id}.png`;
}

function loadImageElement(src: string, crossOrigin?: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('水印资源加载失败'));
    img.src = src;
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.readAsDataURL(blob);
  });
}

/**
 * 将任意图片引用转为 data URL，便于在无 CORS 的 canvas 上绘制（与 App.ensureImageDataUrl 策略一致）
 */
export async function ensureImageDataUrlForWatermark(imageUrl: string): Promise<string> {
  const trimmed = imageUrl.trim();
  if (!trimmed) throw new Error('图片地址为空');

  if (/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(trimmed)) {
    return trimmed;
  }

  if (/^blob:/i.test(trimmed)) {
    const res = await fetch(trimmed);
    if (!res.ok) throw new Error(`图片加载失败 (${res.status})`);
    const dataUrl = await blobToDataUrl(await res.blob());
    if (!/^data:image\//i.test(dataUrl)) throw new Error('图片格式无效');
    return dataUrl;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const res = await fetch(trimmed);
      if (!res.ok) throw new Error(`图片加载失败 (${res.status})`);
      const dataUrl = await blobToDataUrl(await res.blob());
      if (!/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(dataUrl)) {
        throw new Error('图片格式无效');
      }
      return dataUrl;
    } catch {
      const { api } = await import('../services/apiClient');
      const resp = (await api.fetchImageDataUrl(trimmed)) as { dataUrl?: string };
      const dataUrl = resp?.dataUrl;
      if (dataUrl && /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(dataUrl)) {
        return dataUrl;
      }
      throw new Error('无法拉取图片（跨域限制）：请登录后重试，或确认图片来自允许的存储域名');
    }
  }

  if (trimmed.length > 100 && !trimmed.includes('data:')) {
    return `data:image/png;base64,${trimmed}`;
  }

  throw new Error('不支持的图片地址格式');
}

/** 小鱼图标（项目内 SVG，与下载水印同源） */
export const WatermarkFishIcon: React.FC<{ className?: string; pixelSize?: number }> = ({
  className = '',
  pixelSize = 15,
}) => (
  <img
    src={WATERMARK_FISH_SVG_SRC}
    alt=""
    draggable={false}
    aria-hidden
    className={`shrink-0 pointer-events-none select-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] ${className}`}
    style={{ height: pixelSize, width: 'auto', display: 'block' }}
  />
);

interface WatermarkProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

/**
 * 右下角水印：`nemo-logo.svg` 小鱼 + Nemolightlab-AI（与下载 canvas 一致）
 */
export const Watermark: React.FC<WatermarkProps> = ({
  className = '',
  size = 'small',
}) => {
  const sizeMap = {
    small: { text: '13px', fish: 15 },
    medium: { text: '15.6px', fish: 18 },
    large: { text: '18.2px', fish: 21 },
  };

  const { text: textSize, fish: fishPx } = sizeMap[size];

  return (
    <div className={`flex items-center gap-1.5 text-white/80 ${className}`}>
      <WatermarkFishIcon pixelSize={fishPx} />
      <span
        className="font-black tracking-wide whitespace-nowrap"
        style={{
          fontSize: textSize,
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          opacity: 0.9,
        }}
      >
        {WATERMARK_TEXT}
      </span>
    </div>
  );
};

/**
 * 将水印添加到图片上（用于下载）：`nemo-logo.svg` 小鱼矢量栅格化 + Nemolightlab-AI
 * @param imageUrl 图片 URL（base64 或 blob URL）
 * @returns 带有水印的图片 base64 字符串
 */
export const addWatermarkToImage = async (imageUrl: string): Promise<string> => {
  const mainDataUrl = await ensureImageDataUrlForWatermark(imageUrl);
  const [mainImg, logoImg] = await Promise.all([
    loadImageElement(mainDataUrl, false),
    loadImageElement(WATERMARK_FISH_SVG_SRC, false),
  ]);

  const w = mainImg.naturalWidth || mainImg.width;
  const h = mainImg.naturalHeight || mainImg.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('无法创建 canvas 上下文');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(mainImg, 0, 0, w, h);

  const margin = Math.max(8, Math.round(Math.min(w, h) * 0.012));
  const textSize = Math.max(11, Math.min(22, Math.round(Math.min(w, h) * 0.022)));
  const gap = Math.max(5, Math.round(textSize * 0.4));

  const lw = logoImg.naturalWidth || logoImg.width || 500;
  const lh = logoImg.naturalHeight || logoImg.height || 250;

  const fishDrawH = textSize * 1.25;
  const fishDrawW = (lw / lh) * fishDrawH;

  ctx.font = `bold ${textSize}px Arial, sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  const textW = ctx.measureText(WATERMARK_TEXT).width;

  const blockRight = w - margin;
  const blockBottom = h - margin;
  const textX = blockRight;
  const textY = blockBottom;

  const fishLeft = blockRight - textW - gap - fishDrawW;
  const fishTop = blockBottom - fishDrawH + Math.round(textSize * 0.06);

  ctx.drawImage(logoImg, 0, 0, lw, lh, fishLeft, fishTop, fishDrawW, fishDrawH);

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.75)';
  ctx.shadowBlur = Math.max(2, textSize * 0.2);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(1, textSize * 0.06);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
  ctx.fillText(WATERMARK_TEXT, textX, textY);
  ctx.restore();

  return canvas.toDataURL('image/png');
};
